import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, TextField, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { PropertyDTO } from 'util/Types';
import { useMakeInquiry } from 'hooks/useMakeInquiry';
import { useToast } from 'components/Providers/ToastProvider';

interface InquiryModalProps {
    isOpen: boolean;
    onClose: () => void;
    property: PropertyDTO | null;
}

interface InquiryFormData {
    name: string;
    email: string;
    phone: string;
    message: string;
}

const InquiryModal = ({ isOpen, onClose, property }: InquiryModalProps) => {
    const [formData, setFormData] = useState<InquiryFormData>({
        name: '',
        email: '',
        phone: '',
        message: '',
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { mutate: makeInquiry, isPending: isSubmitting } = useMakeInquiry();
    const { showMessage } = useToast();

    const handleInputChange = (field: keyof InquiryFormData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!property?.details?.entityId) {
            showMessage('Error', 'Property information is missing');
            return;
        }

        const inquiryData = {
            fullname: formData.name,
            phoneNumber: formData.phone,
            email: formData.email,
            message: formData.message,
            propertyId: parseInt(property.details.entityId, 10),
        };

        makeInquiry(inquiryData, {
            onSuccess: () => {
                showMessage('Success', 'Your inquiry has been submitted successfully. Someone will contact you shortly.');
                setIsSubmitted(true);
            },
            onError: (error) => {
                showMessage('Error', error.message || 'Failed to submit inquiry. Please try again.');
            },
        });
    };

    const handleClose = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            message: '',
        });
        setIsSubmitted(false);
        onClose();
    };

    if (!property) return null;

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '8px',
                },
            }}
        >
            <DialogTitle sx={{ paddingBottom: '8px' }}>
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Make an Inquiry</h2>
                        <p className="text-sm text-gray-600 mt-1">Interested in "{property.title}"</p>
                    </div>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: 'gray',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent sx={{ paddingTop: '16px' }}>
                {!isSubmitted ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <TextField
                                label="Full Name"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                required
                                fullWidth
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        color: '#666',
                                        '&.Mui-focused': {
                                            color: '#3176bc',
                                        },
                                        '&.MuiFormLabel-filled': {
                                            color: '#3176bc',
                                        },
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#ddd',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3176bc',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3176bc',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label="Phone Number"
                                value={formData.phone}
                                onChange={handleInputChange('phone')}
                                fullWidth
                                variant="outlined"
                                size="small"
                                type="tel"
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        color: '#666',
                                        '&.Mui-focused': {
                                            color: '#3176bc',
                                        },
                                        '&.MuiFormLabel-filled': {
                                            color: '#3176bc',
                                        },
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#ddd',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3176bc',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3176bc',
                                        },
                                    },
                                }}
                            />
                        </div>

                        <TextField
                            label="Email Address"
                            value={formData.email}
                            onChange={handleInputChange('email')}
                            required
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="email"
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: '#666',
                                    '&.Mui-focused': {
                                        color: '#3176bc',
                                    },
                                    '&.MuiFormLabel-filled': {
                                        color: '#3176bc',
                                    },
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#ddd',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#3176bc',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#3176bc',
                                    },
                                },
                            }}
                        />

                        <TextField
                            label="Message"
                            value={formData.message}
                            onChange={handleInputChange('message')}
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={4}
                            placeholder={`I'm interested in learning more about this property. Please contact me with additional information.`}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: '#666',
                                    '&.Mui-focused': {
                                        color: '#3176bc',
                                    },
                                    '&.MuiFormLabel-filled': {
                                        color: '#3176bc',
                                    },
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#ddd',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#3176bc',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#3176bc',
                                    },
                                },
                            }}
                        />

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                onClick={handleClose}
                                variant="outlined"
                                className="flex-1"
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting || !formData.name || !formData.email}
                                className="flex-1"
                                sx={{
                                    backgroundColor: '#3176bc',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: '#2865a3',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#cccccc',
                                    },
                                }}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                            </Button>
                        </div>
                    </form>
                ) : (
                    <div className="text-center py-8">
                        <div className="text-green-600 text-4xl mb-4">✓</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Inquiry Sent Successfully!</h3>
                        <p className="text-gray-600 mb-6">Thank you for your interest. We'll get back to you soon with more information about this property.</p>
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{
                                backgroundColor: '#3176bc',
                                textTransform: 'none',
                                borderRadius: '8px',
                                '&:hover': {
                                    backgroundColor: '#2865a3',
                                },
                            }}
                        >
                            Close
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default InquiryModal;
